import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  FormLabel,
  Select,
  VStack,
  HStack,
  Text,
  Image,
  Spinner,
  useToast,
} from '@chakra-ui/react';
// Supondo que você já tenha configurado o supabaseClient.js
import { supabase } from '../services/supabaseClient';
import { useNavigate } from 'react-router-dom';

const BUCKET = 'videos'; // nome do bucket no Supabase

const AdminMidiaPage = () => {
  const [file, setFile] = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [midias, setMidias] = useState([]);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMidias();
  });

  async function fetchMidias() {
    setLoading(true);
    const { data, error } = await supabase.storage.from(BUCKET).list('', { limit: 100 });
    if (error) {
      toast({ title: 'Erro ao listar mídias', status: 'error' });
      setLoading(false);
      return;
    }
    setMidias(data || []);
    setLoading(false);
  }

  async function handleUpload() {
    if (!file) {
      toast({ title: 'Selecione um arquivo para upload', status: 'warning' });
      return;
    }
    
    setLoading(true);
    
    try {
      // Verificar tamanho do arquivo (limites diferentes para imagens e vídeos)
      const fileSize = file.size / (1024 * 1024); // tamanho em MB
      const maxSize = mediaType === 'video' ? 50 : 20; // 50MB para vídeos (limite do Supabase), 20MB para imagens
      
      if (fileSize > maxSize) {
        toast({ 
          title: 'Arquivo muito grande', 
          description: `O tamanho máximo permitido para ${mediaType === 'video' ? 'vídeos' : 'imagens'} é ${maxSize}MB. Seu arquivo tem ${fileSize.toFixed(2)}MB. Clique em "Ajuda com compressão" abaixo.`, 
          status: 'error',
          duration: 8000,
          isClosable: true
        });
        setLoading(false);
        return;
      }
      
      // Verificar tipo de arquivo
      const ext = file.name.split('.').pop().toLowerCase();
      const validVideoExts = ['mp4', 'webm', 'mov'];
      const validImageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      
      if (mediaType === 'video' && !validVideoExts.includes(ext)) {
        toast({ 
          title: 'Formato de vídeo inválido', 
          description: 'Formatos suportados: MP4, WebM e MOV', 
          status: 'error' 
        });
        setLoading(false);
        return;
      }
      
      if (mediaType === 'image' && !validImageExts.includes(ext)) {
        toast({ 
          title: 'Formato de imagem inválido', 
          description: 'Formatos suportados: JPG, PNG, GIF e WebP', 
          status: 'error' 
        });
        setLoading(false);
        return;
      }
      
      // Criar nome de arquivo seguro para o sistema
      let fileName;
      
      if (name && name.trim() !== '') {
        // Se o usuário forneceu um nome, use-o
        fileName = name.replace(/\s/g, '-')
                       .replace(/[^a-zA-Z0-9\-_]/g, '')
                       .toLowerCase();
      } else {
        // Se não, use apenas o nome do arquivo sem exibir na tela
        fileName = file.name.split('.')[0]
                            .replace(/\s/g, '-')
                            .replace(/[^a-zA-Z0-9\-_]/g, '')
                            .toLowerCase();
      }
      
      const path = `${Date.now()}-${fileName}.${ext}`;
      
      console.log('[AdminMidiaPage] Iniciando upload:', {
        arquivo: file.name,
        tipo: file.type,
        tamanho: fileSize.toFixed(2) + 'MB',
        destino: path
      });
      
      // Realizar upload com retry
      let attempts = 0;
      let uploadError;
      
      while (attempts < 3) {
        const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
          cacheControl: '3600',
          upsert: true, // Permitir sobrescrever se existir
          contentType: file.type,
        });
        
        if (!error) {
          // Upload bem-sucedido
          toast({ title: 'Upload realizado com sucesso!', status: 'success' });
          setFile(null); 
          setName(''); 
          setLink('');
          fetchMidias();
          setLoading(false);
          return;
        }
        
        // Armazenar o erro para mostrar se todas as tentativas falharem
        uploadError = error;
        attempts++;
        
        // Aguardar antes de tentar novamente
        if (attempts < 3) {
          console.log(`[AdminMidiaPage] Tentativa ${attempts} falhou, tentando novamente...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // Se chegou aqui, todas as tentativas falharam
      console.error('[AdminMidiaPage] Erro de upload após 3 tentativas:', uploadError);
      toast({ 
        title: 'Erro ao enviar arquivo', 
        description: uploadError.message || 'Tente novamente com um arquivo menor ou em outro formato',
        status: 'error',
        duration: 5000
      });
    } catch (error) {
      console.error('[AdminMidiaPage] Erro inesperado:', error);
      toast({ 
        title: 'Erro inesperado', 
        description: error.message,
        status: 'error' 
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(filename) {
    setLoading(true);
    const { error } = await supabase.storage.from(BUCKET).remove([filename]);
    if (error) {
      toast({ title: 'Erro ao excluir', status: 'error' });
    } else {
      toast({ title: 'Mídia excluída', status: 'success' });
      fetchMidias();
    }
    setLoading(false);
  }

  return (
    <Box maxW="600px" mx="auto" py={10}>
      <Text fontSize="2xl" fontWeight="bold" mb={6}>Gerenciamento de Mídias (oculto)</Text>
      {loading && (
        <Box textAlign="center" my={4}>
          <Spinner size="xl" color="yellow.500" thickness="4px" />
          <Text mt={2}>Processando... Aguarde.</Text>
        </Box>
      )}
      <VStack spacing={4} align="stretch">
        <Box>
          <FormLabel>Nome do arquivo (opcional)</FormLabel>
          <Input 
            value={name} 
            onChange={e => setName(e.target.value)} 
            placeholder="Nome para identificar o arquivo no painel" 
          />
          <Text fontSize="sm" color="gray.500" mt={1}>
            Apenas para identificar o arquivo no painel administrativo
          </Text>
        </Box>
        <Box>
          <FormLabel>Tipo</FormLabel>
          <Select value={mediaType} onChange={e => setMediaType(e.target.value)}>
            <option value="image">Imagem</option>
            <option value="video">Vídeo</option>
          </Select>
        </Box>
        <Box>
          <FormLabel>Arquivo</FormLabel>
          <Input 
            type="file" 
            accept={mediaType === 'image' ? 'image/*' : 'video/mp4,video/webm,video/quicktime'} 
            onChange={e => setFile(e.target.files[0])} 
          />
          <Text fontSize="sm" color="gray.500" mt={1}>
            {mediaType === 'image' ? 'Formatos: JPG, PNG, GIF (máx. 20MB)' : 'Formatos: MP4, WebM, MOV (máx. 50MB)'}
          </Text>
        </Box>
        <Box>
          <FormLabel>Link externo (opcional)</FormLabel>
          <Input value={link} onChange={e => setLink(e.target.value)} placeholder="https://..." />
        </Box>
        <VStack spacing={3} width="100%">
          <Button
            onClick={handleUpload}
            colorScheme="yellow"
            isLoading={loading}
            loadingText="Enviando..."
            size="lg"
            width="100%"
          >
            Enviar Mídia
          </Button>
          
          {mediaType === 'video' && (
            <Button
              onClick={() => navigate('/video-compression-help')}
              variant="outline"
              colorScheme="blue"
              size="md"
              width="100%"
            >
              Ajuda com compressão de vídeos
            </Button>
          )}
        </VStack>
      </VStack>
      <Box mt={10}>
        <Text fontWeight="bold" mb={2}>Mídias cadastradas:</Text>
        {loading && <Spinner />}
        <VStack align="stretch" spacing={3}>
          {midias.map((m) => {
            // Gerar URL pública/assinada
            const publicURL = supabase.storage.from(BUCKET).getPublicUrl(m.name).publicURL;
            return (
              <Box key={m.name} borderWidth={1} borderRadius="md" p={2} mb={2}>
                <HStack justify="space-between">
                  {m.name.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                    <Image src={publicURL} boxSize="60px" objectFit="cover" alt={m.name} />
                  ) : (
                    <Box boxSize="60px" bg="gray.200" display="flex" alignItems="center" justifyContent="center">
                      <Text fontSize="sm">Vídeo</Text>
                    </Box>
                  )}
                  <Text flex={1} ml={2} fontSize="sm">{m.name}</Text>
                  <Button colorScheme="red" size="xs" onClick={() => handleDelete(m.name)}>
                    Excluir
                  </Button>
                </HStack>
                {/* Exibir URL */}
                <Box mt={2}>
                  <Input value={publicURL} size="sm" isReadOnly width="100%" mb={1} />
                  <Button size="xs" onClick={() => {navigator.clipboard.writeText(publicURL)}}>Copiar URL</Button>
                </Box>
              </Box>
            );
          })}
        </VStack>
      </Box>
    </Box>
  );
};

export default AdminMidiaPage;
